<template>
  <v-expansion-panels
    class="ag-expansion-panel__panels"
    :model-value="isExpanded"
    :readonly="disabled || noData || loading"
    @update:model-value="onHeaderClick"
  >
    <v-expansion-panel
      class="ag-expansion-panel__panel"
      :value="true"
      :class="{ 'ag-expansion-panel__panel--borderless': borderless }"
      :disabled="disabled"
    >
      <v-expansion-panel-title :data-no-data="noData || loading" :data-vertical="isVertical" :class="titleClass">
        <slot name="header" />

        <template #actions>
          <div class="d-flex align-center" :class="{ 'ag-expansion-panel__actions--dense': dense, 'mt-2': isVertical }">
            <slot v-if="isVertical" name="actions" />

            <span class="mr-2 ml-2"> {{ computedActionText }}</span>

            <AGIcon :icon="computedExpandIcon" />

            <slot v-if="!isVertical" name="actions" />
          </div>
        </template>
      </v-expansion-panel-title>

      <v-lazy style="min-width: 0" :options="{ threshold: 0.5 }">
        <v-expansion-panel-text :data-show-divider="!hideSeparator" :class="contentClass">
          <slot name="content" />
        </v-expansion-panel-text>
      </v-lazy>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import AGIcon from '@/components/common/AGIcon.vue';
import { uniqueId } from 'lodash';

export default {
  components: {
    AGIcon,
  },
  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: uniqueId('ag_expansion_panel_'),
    },
    direction: {
      type: String,
      default: 'horizontal',
    },
    borderless: {
      type: Boolean,
      default: false,
    },
    expandIconOpen: {
      type: String,
      default: 'md:keyboard_arrow_down',
    },
    expandIconClose: {
      type: String,
      default: 'md:keyboard_arrow_up',
    },
    expandTextOpen: {
      type: String,
      default: '',
    },
    expandTextClose: {
      type: String,
      default: '',
    },
    hideSeparator: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    noData: {
      type: Boolean,
      default: false,
    },
    titleClass: {
      type: String,
      default: '',
    },
    contentClass: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      internalModelValue: true,
    };
  },

  computed: {
    localModelValue: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', !!val);
        this.internalModelValue = !!val;
      },
    },
    isExpanded() {
      return this.disabled || this.noData ? undefined : this.internalModelValue;
    },
    computedActionText() {
      return this.isExpanded
        ? this.expandTextClose || this.expandTextOpen
        : this.expandTextOpen || this.expandTextClose;
    },
    computedExpandIcon() {
      return this.isExpanded ? this.expandIconClose : this.expandIconOpen;
    },
    isVertical() {
      return this.direction === 'vertical';
    },
    pathForLocalStorage() {
      return `expansion_panel_${this.$route.name}_${this.id}`;
    },
  },

  created() {
    this.internalModelValue = this.modelValue;
    this.getStateFromLocalStorage();
  },

  methods: {
    onHeaderClick(value) {
      this.localModelValue = value;

      localStorage.setItem(this.pathForLocalStorage, value + '');
    },
    getStateFromLocalStorage() {
      const expandedState = localStorage.getItem(this.pathForLocalStorage);

      if (expandedState && this.noData) {
        this.localModelValue = expandedState;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ag-expansion-panel__no-data {
  margin-left: 6px;
  font-size: $font-size-xs;
  font-weight: $font-weight-bold;
}

.ag-expansion-panel__panels {
  z-index: unset;
}

.ag-expansion-panel__panel {
  border: 1px solid $neutral-color-low-dark;

  &--borderless {
    border: none;
  }

  .ag-expansion-panel__actions--dense {
    font-size: $font-size-xxxs;
  }

  ::v-deep(.v-expansion-panel__shadow) {
    display: none;
  }

  ::v-deep(.v-expansion-panel-title) {
    padding: $spacing-05;
  }

  ::v-deep(.v-expansion-panel-title__icon) {
    color: $feedback-color-information-medium;
    font-size: $font-size-xs;
    font-weight: $font-weight-regular;
  }

  .v-expansion-panel-title {
    background-color: $neutral-color-low-medium;
    color: rgb(var(--v-theme-menuBackground));
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
    border-radius: $border-radius-sm;

    &[data-vertical='true'] {
      flex-direction: column;
      gap: $spacing-03;
    }

    &[data-no-data='true'] {
      background-color: $neutral-color-low-dark;
      color: $neutral-color-high-medium;
      font-style: normal;
      font-weight: $font-weight-regular;
      letter-spacing: 0.2px;
      cursor: auto;

      ::v-deep(.v-expansion-panel-title__icon) {
        color: $neutral-color-high-medium;
      }
    }
  }

  .v-expansion-panel-title--active {
    border-radius: $border-radius-sm $border-radius-sm 0 0;
  }

  .v-expansion-panel-text {
    background-color: $neutral-color-low-medium;
    border-bottom-left-radius: $border-radius-sm;
    border-bottom-right-radius: $border-radius-sm;
    ::v-deep(.v-expansion-panel-text__wrapper) {
      padding: 0 $spacing-05 $spacing-05 $spacing-05;
    }

    &[data-show-divider='true'] {
      border-top: 1px solid $neutral-color-low-dark;
      ::v-deep(.v-expansion-panel-text__wrapper) {
        padding: $spacing-05;
      }
    }
  }

  ::v-deep(.v-expansion-panel-title__overlay) {
    display: none;
  }
}
</style>
